import { useEffect, useSyncExternalStore } from "react";

/**
 * Viral Challenges — local, ephemeral progress tracking.
 * No DB writes; persisted to localStorage so users see streak.
 */
export type Challenge = {
  id: string;
  emoji: string;
  titleKey: string;
  descKey: string;
  target: number;
  reward: string;
};

export const CHALLENGES: Challenge[] = [
  { id: "predict_3", emoji: "🎯", titleKey: "ch_predict_3_t", descKey: "ch_predict_3_d", target: 3, reward: "+150 XP" },
  { id: "hype_dom", emoji: "🔥", titleKey: "ch_hype_t", descKey: "ch_hype_d", target: 10, reward: "+200 XP" },
  { id: "beat_bra", emoji: "🏆", titleKey: "ch_beat_bra_t", descKey: "ch_beat_bra_d", target: 5, reward: "Victory Card" },
  { id: "top_1pct", emoji: "👑", titleKey: "ch_top_t", descKey: "ch_top_d", target: 100, reward: "Top 1% badge" },
];

const STORAGE = "vs_challenges_progress_v1";
type Progress = Record<string, number>;
let progress: Progress = {};
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

function load() {
  if (typeof window === "undefined") return;
  try {
    const raw = window.localStorage.getItem(STORAGE);
    if (raw) progress = JSON.parse(raw);
  } catch { /* noop */ }
}
function persist() {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(STORAGE, JSON.stringify(progress)); } catch { /* noop */ }
}
load();

export function bumpChallenge(id: string, by = 1) {
  progress = { ...progress, [id]: Math.max(0, (progress[id] ?? 0) + by) };
  persist();
  emit();
}

const subscribe = (fn: () => void) => { listeners.add(fn); return () => { listeners.delete(fn); }; };
const snap = () => progress;

export function useChallengesProgress() {
  return useSyncExternalStore(subscribe, snap, snap);
}

/** Demo nudge: gently advance the "hype" challenge while the user is on the page. */
export function useDemoNudge(id: string, intervalMs = 12_000) {
  useEffect(() => {
    const t = window.setInterval(() => bumpChallenge(id, 1), intervalMs);
    return () => window.clearInterval(t);
  }, [id, intervalMs]);
}
